import React, { useMemo } from 'react';
import { Developer } from '../types/DeveloperTypes';
import BurndownChart, { BurndownDataset } from './BurndownChart';
import IconButton from './AIAnalysisButton';

interface DeveloperBurndownChartProps {
  developer: Developer;
  onOpenAnalysisModal: () => void;
}

// Developer Burndown Chart Component
const DeveloperBurndownChart: React.FC<DeveloperBurndownChartProps> = ({ developer, onOpenAnalysisModal }) => {
  // Material Icons font is now loaded by the IconButton component

  // Get all iterations with burndown data
  const iterationsWithBurndown = useMemo(() => {
    if (!developer.IterationData) return [];
    
    return developer.IterationData.filter(iteration => iteration.BurndownData)
      .map(iteration => ({
        ...iteration,
        parsedBurndown: JSON.parse(iteration.BurndownData || '{}')
      }))
      .sort((a, b) => new Date(a.StartDate).getTime() - new Date(b.StartDate).getTime());
  }, [developer.IterationData]);
  
  // If no burndown data is available
  if (iterationsWithBurndown.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
        <p>No burndown data available for this developer.</p>
      </div>
    );
  }
  
  // Convert to the format expected by the BurndownChart component
  const burndownDatasets: BurndownDataset[] = iterationsWithBurndown.map(iteration => ({
    labels: Array.from({ length: 11 }, (_, i) => `${i * 10}%`),
    planned: iteration.parsedBurndown.planned || [],
    actual: iteration.parsedBurndown.actual || [],
    sprintInfo: {
      id: iteration.IterationId,
      name: iteration.IterationName
    }
  }));
  
  return (
    <div>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '16px'
      }}>
        <h4 style={{ 
          margin: 0,
          fontSize: '16px',
          color: '#3949ab'
        }}>
          {/* {developer.DeveloperName}'s Burndown Performance Across Iterations */}
        </h4>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <IconButton
            onClick={onOpenAnalysisModal}
            icon="analytics"
            variant="default"
            size="medium"
            title="AI Analysis"
          />
          {/* <span style={{ fontSize: '14px', color: '#3949ab' }}>AI Analysis</span> */}
        </div>
      </div>
      
      <div style={{ height: '400px', marginBottom: '20px' }}>
        <BurndownChart 
          burndownDatasets={burndownDatasets} 
          height={400}
        />
      </div>
      <div style={{ marginTop: '20px' }}>
        <p>
          This chart shows {developer.DeveloperName}'s burndown performance across multiple iterations. 
          The dashed lines represent the ideal burndown rate, while the solid lines show actual progress.
        </p>
        <div style={{ 
          padding: '12px', 
          backgroundColor: '#f5f5f5', 
          borderRadius: '8px',
          marginTop: '12px'
        }}>
          
          <p style={{ margin: '4px 0 0 0' }}>
            Date Range: {new Date(iterationsWithBurndown[0].StartDate).toLocaleDateString()} - {new Date(iterationsWithBurndown[iterationsWithBurndown.length-1].EndDate).toLocaleDateString()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default DeveloperBurndownChart;
